<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0" title="">
      <v-row align="center" class="mb-8" no-gutters>
        <v-col cols="2">
          <v-combobox class="mr-2" v-model="selected_maximum_treatment_phase" :items="select_maximum_treatment_phase_items"
                      label="Maximum number of treatment phases to display"
                      hint="Controls the maximum number of treatment phases to display. Used to display additional
                      Subsequent phases."
                      :persistent-hint="true"></v-combobox>
        </v-col>
        <v-col cols="2">
          <v-checkbox v-model="panAndZoomEnabled" label="Enable pan and zoom"
                      hide-details></v-checkbox>
        </v-col>
        <v-col cols="4">
          <v-spacer></v-spacer>
        </v-col>
        <v-col cols="1">
          <v-btn class="ml-2 mt-2" color="secondary" @click.stop="this.sanKeyMoveUpInZoom"
                 :disabled="this.selected_previous_phase.length === 0">Move up</v-btn>
        </v-col>
        <v-col cols="1">
          <v-btn class="ml-2 mt-2" color="secondary" @click.stop="this.sanKeyResetZoom">Reset</v-btn>
        </v-col>
      </v-row>
      <v-row class="mb-8" align="center" no-gutters>
        <v-col cols="12">
          <GChart :settings="{ packages: ['sankey'] }" type="Sankey"
                  :data="sanKeyChartData"
                  :options="sanKeyChartOptions"
                  id="sanKeyChart"
                  @ready="sanKeyChartReady"
                  v-if="sanKeyChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
      <v-row class="mb-8" align="start" no-gutters>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0" title="Legend for major nodes (full view)">
            <v-list density="compact" nav>
              <v-list-item v-for="item in fullSankeyLegendItems" :key="item" prepend-icon="mdi:mdi-minus">
                {{ item }}
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mb-8" align="center" no-gutters>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0" title="Treatment sequence (focused view)">
            <v-card-subtitle class="text-wrap">Simplified treatment sequence based on the validated aggregated transition table.</v-card-subtitle>
            <v-row align="center" class="mt-2 mb-4" no-gutters>
              <v-col cols="10">
                <v-spacer></v-spacer>
              </v-col>
              <v-col cols="1">
                <v-btn class="ml-2" color="secondary" @click.stop="this.focusedSanKeyMoveUpInZoom"
                       :disabled="this.selected_previous_focused_phase.length === 0">Move up</v-btn>
              </v-col>
              <v-col cols="1">
                <v-btn class="ml-2" color="secondary" @click.stop="this.focusedSanKeyResetZoom">Reset</v-btn>
              </v-col>
            </v-row>
            <GChart :settings="{ packages: ['sankey'] }" type="Sankey"
                    :data="focusedSanKeyChartData"
                    :options="sanKeyChartOptions"
                    id="focusedSanKeyChart"
                    @ready="focusedSanKeyChartReady"
                    v-if="focusedSanKeyChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="mb-8" align="start" no-gutters>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0" title="Legend for major nodes">
            <v-list density="compact" nav>
              <v-list-item v-for="item in focusedSankeyLegendItems" :key="item" prepend-icon="mdi:mdi-minus">
                {{ item }}
              </v-list-item>
            </v-list>
            <v-card-text class="pt-0 text-wrap">
              <p class="mb-2">Among people whose first drug after diagnosis was ACHEI alone, about one quarter remained on ACHEI alone through follow-up or death, one third added other drugs, and one third switched to corticosteroids or other drugs.</p>
              <p class="mb-0">Among people whose first drug after diagnosis was corticosteroids alone, about half remained on corticosteroids alone through follow-up or death, while one quarter switched to ACHEI.</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row align="start" class="mb-8" no-gutters>
        <v-col cols="5">
          <v-data-table
              :headers="[{title: 'Description', align: 'start', key: 'line', sortable: false}]"
              :items="treatmentDescription.map(data => ({line: data}))"
              density="compact"
              :fixed-header="true"
              :items-per-page="0"
              class="no-row-borders">
            <template #bottom></template>
            <template #no-data></template>
            <template v-slot:item="{ item }">
              <div v-if="item.line !== ''" class="ms-4">
                {{ item.line }}
              </div>
              <div :style="{ height: '12px'}" v-else></div>
            </template>
          </v-data-table>
        </v-col>
        <v-col cols="1">
          <v-divider vertical></v-divider>
        </v-col>
        <v-col cols="6">
          <div>
            <v-data-table :headers="treatmentLegendHeaders"
                          :items="treatmentLegend"
                          density="compact"
                          :fixed-header="true"
                          :items-per-page="0"
                          :row-props="rowProps"
                          @click:row="handleLegendRowClicked"
                          class="no-row-borders allow-line-breaks striped-table">
              <template #bottom></template>
              <template #no-data></template>
            </v-data-table>
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="6">
          <v-card class="mx-auto" elevation="0" title="Time from diagnosis to first treatment in the total cohort">
            <v-table density="compact" class="striped-table">
              <thead>
              <tr>
                <th class="text-left">Treatments</th>
                <th class="text-right">Number of patients (%)</th>
                <th class="text-right">Months from diagnosis to treatment, median (95% CI)</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in timeToTreatmentTableItems" :key="item.treatment">
                <td>{{ item.treatment }}</td>
                <td class="text-right">{{ item.numberOfPatients }}</td>
                <td class="text-right">{{ item.monthsFromDiagnosisToTreatment }}</td>
              </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import svgPanZoom from 'svg-pan-zoom';

const SANKEY_STATE_READY = 0;
const SANKEY_STATE_POPPED = 1;

export default defineComponent({
  name: 'NeurologicalDiseaseTreatmentPaths',
  props: {
    icd: String,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    treatmentDescription: [],
    treatmentLegendHeaders: [
      {
        title: 'Legend',
        align: 'start',
        key: 'treatment_name',
        sortable: false
      },
      {
        title: '',
        align: 'start',
        key: 'treatment_description',
        sortable: false
      }
    ],
    treatmentLegend: [],
    fullSankeyLegendItems: [
      'ACHEI: Acetylcholinesterase Inhibitor.',
      'ANTIB: Antibiotics.',
      'ACHEI+CCS: Acetylcholinesterase Inhibitor & Corticosteroids.',
      'CCS: Corticosteroids.',
      'ACHEI+Other: Acetylcholinesterase Inhibitor & Other drugs.',
      'OTHER: Other drugs.',
      'Death',
      'ACHEI+CCS +Other: Acetylcholinesterase Inhibitor & Corticosteroids & Other drugs.',
      'ACHEI+ ANTIB: Acetylcholinesterase Inhibitor & Antibiotics.',
      'PEX: Plasma Exchange.',
      'RIX: Rituximab.'
    ],
    focusedSankeyLegendItems: [
      'ACHEI: Acetylcholinesterase Inhibitor.',
      'CCS: Corticosteroids.',
      'ACHEI+CCS: Acetylcholinesterase Inhibitor and Corticosteroids.',
      'ACHEI+OTHER: Acetylcholinesterase Inhibitor and Other drugs.',
      'OTHER: Other drugs.',
      'Death.',
      'End of follow-up.',
      'Combo3+: At least three types of drugs among ACHEI, CCS, RIX, and OTHER.',
      'PEX: Plasma exchange (representing PEX alone and PEX with any additional drugs).',
      'RIX: Rituximab.',
      'CCS+OTHER: Corticosteroids and Other drugs.'
    ],
    select_maximum_treatment_phase_items: [
      {
        title: 'Two',
        value: 3
      },
      {
        title: 'Three',
        value: 4
      },
      {
        title: 'Four',
        value: 5
      },
      {
        title: 'Five',
        value: 6
      },
      {
        title: 'Six',
        value: 7
      },
      {
        title: 'Seven',
        value: 8
      },
      {
        title: 'Eight',
        value: 9
      },
      {
        title: 'Nine',
        value: 10
      },
      {
        title: 'Ten',
        value: 11
      },
      {
        title: 'Eleven',
        value: 12
      },
      {
        title: 'Twelve',
        value: 13
      },
      {
        title: 'Thirteen',
        value: 14
      },
      {
        title: 'Fourteen',
        value: 15
      },
      {
        title: 'Fifteen',
        value: 16
      },
      {
        title: 'Sixteen',
        value: 17
      },
      {
        title: 'Seventeen',
        value: 18
      },
      {
        title: 'Eighteen',
        value: 19
      },
      {
        title: 'Nineteen',
        value: 20
      },
      {
        title: 'Twenty',
        value: 21
      }
    ],
    selected_maximum_treatment_phase: {
      title: 'Three',
      value: 4
    },
    selected_phase: {},
    selected_focused_phase: {},
    additional_phase_filter: undefined,
    selected_previous_phase: [],
    selected_previous_sankey: [],
    selected_previous_focused_phase: [],
    sankey_state: SANKEY_STATE_READY,
    focused_sankey_state: SANKEY_STATE_READY,
    panAndZoomEnabled: false,
    sanKeyPanZoom: undefined,
    sanKeyChartData: [],
    focusedSanKeyChartData: [],
    sanKeyTransitionData: [],
    focusedSanKeyTransitionData: [],
    sanKeyChartOptions: {
      height: 800,
      sankey: {
        node: {
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          label: {
            fontSize: 14,
            color: '#ffffff',
            bold: true
          }
        },
        link: {
          colorMode: 'gradient',
          colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
          interactivity: true
        }
      },
      tooltip: {
        isHtml: true
      }
    },
    timeToTreatmentTableItems: [
      {
        treatment: 'Acetylcholinesterase inhibitor (ACHEI)',
        numberOfPatients: '4669 (74.2)',
        monthsFromDiagnosisToTreatment: '0.7 (0.7, 0.7)'
      },
      {
        treatment: 'Corticosteroids (CCS)',
        numberOfPatients: '3862 (61.4)',
        monthsFromDiagnosisToTreatment: '7.2 (6.2, 8.9)'
      },
      {
        treatment: 'Plasma exchange',
        numberOfPatients: '178 (2.8)',
        monthsFromDiagnosisToTreatment: '9.2 (5.3, 15.4)'
      },
      {
        treatment: 'Rituximab (cytostatic therapy)',
        numberOfPatients: '227 (3.6)',
        monthsFromDiagnosisToTreatment: '55.5 (37.5, 67.0)'
      },
      {
        treatment: 'Antibiotics',
        numberOfPatients: '4476 (71.2)',
        monthsFromDiagnosisToTreatment: '27.8 (26.0, 29.2)'
      },
      {
        treatment: 'Other drugs',
        numberOfPatients: '2113 (33.6)',
        monthsFromDiagnosisToTreatment: '9.5 (8.5, 11.2)'
      }
    ]
  }),
  watch: {
    selected_phase() {
      this.loadDynamicData()
    },
    selected_focused_phase() {
      this.loadDynamicData()
    },
    additional_phase_filter() {
      this.loadDynamicData()
    },
    selected_maximum_treatment_phase() {
      this.loadDynamicData()
    },
    panAndZoomEnabled() {
      this.loadDynamicData()
    }
  },
  methods: {
    async loadTreatmentDescription() {
      let data = axios.get('neurological-disease/treatment-description', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
        }
      });
      return data.then((response) => {
        this.treatmentDescription = response.data
      });
    },
    async loadTreatmentLegend() {
      let data = axios.get('neurological-disease/treatment-legend', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
        }
      });
      return data.then((response) => {
        this.treatmentLegend = response.data
      });
    },
    async loadSanKeyChartData() {
      let postData = {
        icd: this.icd,
        pre_phase: this.selected_phase.prePhase,
        post_phase: this.selected_phase.postPhase,
        phase: this.selected_phase.phase,
        additional_phase_filter: this.additional_phase_filter,
        maximum_treatment_phase: this.selected_maximum_treatment_phase.value
      }
      return axios.post('neurological-disease/sankey-data',
          postData, {
            headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`}
          });
    },
    async loadFocusedSanKeyChartData() {
      return axios.get('neurological-disease/sankey-focused-data', {
        headers: {Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}`},
        params: {
          icd: this.icd,
          pre_phase: this.selected_focused_phase.prePhase,
          post_phase: this.selected_focused_phase.postPhase,
          phase: this.selected_focused_phase.phase
        }
      });
    },
    async sanKeyChartReady(chart, google) {
      const setSanKeyData = (selectedRow, selectPrevious) => {
        let selected_phase = this.sanKeyTransitionData[selectedRow]
        if ((selected_phase.prePhase !== 'Death') && (selected_phase.prePhase !== 'Censored')
            && (selected_phase.postPhase !== 'Censored') && (selected_phase.postPhase !== 'Subsequent phases')) {
          this.selected_previous_phase.push(this.selected_phase)
          this.selected_previous_sankey.push(selectPrevious)
          this.selected_phase = selected_phase
        }
      }
      const isSankeyStateReady = () => this.sankey_state === SANKEY_STATE_READY
      const setSankeyStateReady = () => this.sankey_state = SANKEY_STATE_READY
      const selectHandler = function () {
        const selection = chart.getSelection()
        const selectedItem = selection[0];
        if (selectedItem && isSankeyStateReady()) {
          setSanKeyData(selectedItem.row, selection)
        }
        setSankeyStateReady()
      }

      const popPreviousSankey = () => {
        if (this.selected_previous_sankey.length === this.selected_previous_phase.length + 1) {
          return this.selected_previous_sankey.pop()
        }
      }
      const setSankeyStatePopped = () => this.sankey_state = SANKEY_STATE_POPPED
      const getPanAndZoomEnabled = () => this.panAndZoomEnabled
      const getSanKeyPanZoom = () => this.sanKeyPanZoom
      const setSanKeyPanZoom = (panZoom) => {
        this.sanKeyPanZoom = panZoom
      }
      const readyHandler = function () {
        let previousSankey = popPreviousSankey()
        if (previousSankey) {
          setSankeyStatePopped()
          chart.setSelection(previousSankey)
        }
        const svg = document.querySelector('#sanKeyChart svg');
        if (svg && getPanAndZoomEnabled()) {
          if (getSanKeyPanZoom()?.destroy) {
            getSanKeyPanZoom().destroy();
          }
          setSanKeyPanZoom(svgPanZoom(svg, {
            controlIconsEnabled: true,
            fit: true,
            center: true,
            minZoom: 0.5,
            maxZoom: 10,
            zoomScaleSensitivity: 0.3
          }));
        }
      }

      google.visualization.events.addListener(chart, 'ready', readyHandler)
      google.visualization.events.addListener(chart, 'select', selectHandler);
    },
    async focusedSanKeyChartReady(chart, google) {
      const setFocusedSanKeyData = (selectedRow) => {
        let selectedPhase = this.focusedSanKeyTransitionData[selectedRow]
        if (selectedPhase && selectedPhase.phase !== null && selectedPhase.postPhase !== 'T>=3'
            && selectedPhase.postPhase !== 'End of follow-up' && selectedPhase.postPhase !== 'Censored') {
          this.selected_previous_focused_phase.push(this.selected_focused_phase)
          this.selected_focused_phase = selectedPhase
        }
      }
      const isFocusedSankeyStateReady = () => this.focused_sankey_state === SANKEY_STATE_READY
      const setFocusedSankeyStateReady = () => this.focused_sankey_state = SANKEY_STATE_READY
      const selectHandler = function () {
        const selection = chart.getSelection()
        const selectedItem = selection[0];
        if (selectedItem && isFocusedSankeyStateReady()) {
          setFocusedSanKeyData(selectedItem.row)
        }
        setFocusedSankeyStateReady()
      }

      google.visualization.events.addListener(chart, 'select', selectHandler);
    },
    async sanKeyMoveUpInZoom() {
      if (this.selected_previous_phase.length > 0) {
        this.selected_phase = this.selected_previous_phase.pop()
      }
    },
    async sanKeyResetZoom() {
      this.sankey_state = SANKEY_STATE_READY
      this.selected_phase = {}
      this.additional_phase_filter = undefined
      this.selected_previous_phase=[]
      this.selected_previous_sankey=[]
    },
    async focusedSanKeyMoveUpInZoom() {
      if (this.selected_previous_focused_phase.length > 0) {
        this.selected_focused_phase = this.selected_previous_focused_phase.pop()
      }
    },
    async focusedSanKeyResetZoom() {
      this.focused_sankey_state = SANKEY_STATE_READY
      this.selected_focused_phase = {}
      this.selected_previous_focused_phase=[]
    },
    rowProps(data) {
      return {
        class: "ciencia_row" + ((this.additional_phase_filter === data.item.treatment_name) ? ' ciencia_selected_row' : ''),
      };
    },
    handleLegendRowClicked(event, row) {
      this.additional_phase_filter = (row.item.treatment_name === this.additional_phase_filter) ? undefined : row.item.treatment_name;
    },
    async loadStaticData() {
      await Promise.all([
        this.loadTreatmentDescription(),
        this.loadTreatmentLegend()
      ]).then(() => {
        console.log('Static data loaded: NeurologicalDiseaseTreatmentPaths')
      })
    },
    async loadDynamicData() {
      await Promise.all([
        this.loadSanKeyChartData(),
        this.loadFocusedSanKeyChartData()
      ]).then(responses => {
        this.sanKeyChartData = responses[0].data.graphData;
        this.focusedSanKeyChartData = responses[1].data.graphData ?? []
        this.sanKeyTransitionData = responses[0].data.transitionData;
        this.focusedSanKeyTransitionData = responses[1].data.transitionData ?? [];
        console.log('Dynamic data loaded: NeurologicalDiseaseTreatmentPaths')
      })
    }
  },
  mounted() {
    this.loadStaticData().then(() => {
      return this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.loadDynamicData()
    }
  }
});
</script>

<style scoped>
.no-row-borders :deep(.v-data-table__tr) {
  border-bottom: none !important;
  box-shadow: none !important;
}

.no-row-borders :deep(.v-data-table__td) {
  border-bottom: none !important;
  box-shadow: none !important;
}

.no-row-borders :deep(.v-data-table__th) {
  border-bottom: none !important;
}

.allow-line-breaks :deep(.v-data-table__td) {
  white-space: pre-line;
}

.striped-table :deep(tbody tr:nth-child(odd):not(:hover):not(.ciencia_selected_row)) {
  background-color: rgba(0, 0, 0, .10);
}

:deep .ciencia_row:hover {
  background-color: #566c7b;
}

:deep .ciencia_selected_row {
  background-color: #566c7b;
}
</style>
